import VendNumber, { add, vn } from 'vend-number'
import {
  ApiRetailerPaymentType,
  ApiTender,
  ApiTenderType,
  ApiTransaction,
} from '@vend/types'
import { PAYMENT_TYPES } from '@vend/business-components'
import { removeFromStorage } from '@vend/utilities'
import { KLARNA_PAYMENT_TYPES } from '../components/payments/integrated-payment/integrated-payment-types.constant'
import { Payment } from '../models/payment'
import { Totals } from '../models/sale'
import {
  Tender,
  TenderType,
  Transaction,
} from '../components/payments/swiper-payment-modal/react/queries/Transaction'
import { ReduxCurrentSale } from '../state/slices/currentSale/currentSaleSlice'
import { ReceiptTender } from '../managers/payment-manager'
import { ACTIVE_LSPAY_TRANSACTION } from '../constants/payments'
import { ClosurePayment } from '../util/register-closure-types'
import { isAccountBalancePaymentPaid } from './sale'
import { isKlarnaPaymentType } from './payment-types/klarna'

/**
 * Get all payments for a particular payment type.
 *
 * @param params
 *        @param params.payments
 *               Collection of payments.
 *
 *        @param params.paymentTypeId
 *               The payment type id to evaluate.
 *
 * @return Payments with the requested payment type id
 */
export const getAllPaymentsForPaymentType = ({
  payments = [],
  paymentTypeId,
}: {
  payments: Payment[]
  paymentTypeId: number
}) => payments.filter(payment => payment.paymentType.type_id === paymentTypeId)

/**
 * Determine if a particular payment type exists in a collection of payments.
 *
 * @param params
 *        @param params.payments
 *               Collection of payments.
 *
 *        @param params.paymentTypeId
 *               The payment type id to evaluate.
 *
 * @return True if the payment type does exist in the collection of payments, false otherwise.
 */
export const hasPaymentsForPaymentType = ({
  payments = [],
  paymentTypeId,
}: {
  payments: Payment[]
  paymentTypeId: number
}) => payments.some(payment => payment.paymentType.type_id === paymentTypeId)

/**
 * Determine how many payments of a particular payment type exist in a collection of payments.
 *
 * @param params
 *        @param params.payments
 *               Collection of payments.
 *
 *        @param params.paymentTypeId
 *               The payment type id to evaluate.
 *
 * @return The number of payments for the given payment type found in the given collection of payments
 */
export const numberOfPaymentsForPaymentType = ({
  payments = [],
  paymentTypeId,
}: {
  payments: Payment[]
  paymentTypeId: number
}): number => {
  let count = 0
  payments.forEach(payment => {
    if (payment.paymentType.type_id === paymentTypeId) count++
  })
  return count
}

/**
 * Determine if other payment type exists in a collection of payments.
 *
 * @param params
 *        @param params.payments
 *               Collection of payments.
 *
 *        @param params.paymentTypeId
 *               The payment type id to evaluate.
 *
 * @return True if the other payment type does exist in the collection of payments, false otherwise.
 */
export const hasPaymentsForOtherPaymentType = ({
  payments = [],
  paymentTypeId,
}: {
  payments: Payment[]
  paymentTypeId: number
}) => payments.some(payment => payment.paymentType.type_id !== paymentTypeId)

/**
 * Determines if any of the given payments has been made via a payment type
 * that is not one of those in the given payment type ids.
 *
 * @param params
 *        @param params.payments
 *               Collection of payments.
 *
 *        @param params.paymentTypeIds
 *               Collection of excluded payments.
 *
 * @return True if at least one payment exists which is not in given types, false otherwise.
 */
export const hasPaymentsExcludingPaymentTypes = ({
  payments = [],
  paymentTypeIds,
}: {
  payments: Payment[]
  paymentTypeIds: number[]
}) => {
  return payments.some(payment => {
    return !paymentTypeIds.some(
      (typeId: number) => payment.paymentType.type_id === typeId
    )
  })
}
/**
 * Determine if a payment is of type 'cash concealed'.
 *
 * @param  payment
 *         The payment to test
 *
 * @return True if the payment is of type 'cash concealed'
 */
export const isCashConcealedPaymentType = (
  payment: Payment | ClosurePayment
) => {
  const paymentType = payment.paymentType
  switch (paymentType.type_id) {
    case PAYMENT_TYPES.CASH:
      return Boolean(
        paymentType.config && paymentType.config.conceal_cash_totals
      )

    case PAYMENT_TYPES.CASH_CONCEALED:
      return true
  }
  return false
}

/**
 * Determine if the payment is a Gift Card payment type
 *
 * @param payment
 * @return True if the payment is a Gift Card payment type
 */
export const isGiftCardPaymentType = (payment: Payment) =>
  payment.paymentType.type_id === PAYMENT_TYPES.GIFT_CARD

/**
 * Determine if the payment is a Gift Card payment type
 *
 * @param payment
 * @return True if the payment is a Gift Card payment type
 */
export const isBigCommerceGiftCertPaymentType = (payment: Payment) =>
  payment.paymentType.type_id === PAYMENT_TYPES.BIGCOMMERCE_GIFT_CERT

const READ_ONLY_PAYMENT_TYPES = [
  PAYMENT_TYPES.GIFT_CARD,
  PAYMENT_TYPES.STORE_CREDIT,
  PAYMENT_TYPES.LOYALTY,
  PAYMENT_TYPES.ROUNDING,
  PAYMENT_TYPES.CURRENCY_ADJUST,
  PAYMENT_TYPES.BIGCOMMERCE_GIFT_CERT,
  ...KLARNA_PAYMENT_TYPES,
]

/**
 * Determine if a payment is of a read only type. Used to determine whether the table field is
 * locked.
 *
 * @param  payment
 *         The payment to test
 *
 * @return True if payment type is defined read only
 */
export const isReadOnlyPaymentType = (payment: Pick<Payment, 'paymentType'>) =>
  READ_ONLY_PAYMENT_TYPES.includes(payment.paymentType.type_id)

/**
 * Get the total amount of tips from a list of payments.
 *
 * @param payments
 *        the list of payments to get the total tip amount from
 *
 * @return The total amount of tips from the list of payments
 */
export const getTotalTipAmount = (payments: Payment[]): number => {
  return payments.reduce(
    (acc: number, cur: Payment) => add(acc, vn(cur.tipAmount)),
    0
  )
}

/**
 * Get the total amount of tips from a list of tenders.
 *
 * @param tenders
 *        the list of tenders to get the total tip amount from.
 *
 * @return The total amount of tips from the list of tenders.
 */
export const getTotalTipAmountFromTenders = (tenders: ApiTender[]): number => {
  const filteredTenders = tenders
    .filter(tender => tender.type === 'TIP')
    .map(tender => vn(tender.amount))

  if (filteredTenders.length > 0) {
    return add(...filteredTenders)
  }
  return 0
}

/**
 * Get the total amount of surcharges from a list of tenders.
 *
 * @param tenders
 *        the list of tenders to get the total surcharge amount from.
 *
 * @return The total amount of surcharge from the list of tenders.
 */
export const getTotalSurchargeAmountFromTenders = (
  tenders: ApiTender[]
): number => {
  const filteredSurchargeTenders = tenders
    .filter(tender => tender.type === 'SURCHARGE')
    .map(tender => vn(tender.amount))
  if (filteredSurchargeTenders.length > 0) {
    return add(...filteredSurchargeTenders)
  }
  return 0
}
/**
 * Get the surcharge rule from a list of tenders.
 * @param tenders
 * @returns The surcharge rule from the surcharge tender.
 */
export const getSurchargeRuleFromTenders = (tenders: ApiTender[]): string => {
  const surchargeTender = tenders.find(tender => tender.type === 'SURCHARGE')
  return surchargeTender ? surchargeTender.provider_fields.surcharge_rule : ''
}

const MAIN_TENDER_TYPES: TenderType[] = ['SALE', 'REFUND', 'REVERSAL']

/**
 * Returns the main tender from a list of tenders.
 * A main tender contains the primary transaction information for a transaction.
 * It is the first tender that was found with a type of `sale`, `refund` or `reversal`.
 *
 * @param tenders
 *        a list of Tender.
 * @returns The main tender. Returns null if there was none.
 */
export const getMainTender = (tenders: Tender[]) => {
  const saleTender = tenders.find(tender =>
    MAIN_TENDER_TYPES.includes(tender.type)
  )
  return saleTender || null
}

/**
 * The tender types we consider to be the "main" tender in a transaction
 */
const MAIN_API_TENDER_TYPES: ApiTenderType[] = ['SALE', 'REFUND', 'REVERSAL']

/**
 * @deprecated helper function on ApiTender type used for /api/2.0
 * Returns the main api tender from a list of api tenders.
 * A main api tender contains the primary transaction information for a transaction.
 * It is the first api tender that was found with a type of `sale`, `refund` or `reversal`.
 *
 * @param tenders
 *        a list of ApiTender.
 * @returns The main api tender. Returns null if there was none.
 */
export const getMainApiTender = (tenders: ApiTender[]): ApiTender | null => {
  const saleTender = tenders.find(tender =>
    MAIN_API_TENDER_TYPES.includes(tender.type)
  )

  return saleTender || null
}

/**
 * Returns the main tender from a list of receipt tenders.
 * A main api tender contains the primary transaction information for a transaction.
 * It is the first api tender that was found with a type of `sale`, `refund` or `reversal`.
 *
 * @param tenders
 *        a list of receipt tenders.
 * @returns The main api tender. Returns null if there was none.
 */
export const getMainTenderForReceipt = (tenders: ReceiptTender[]) => {
  const saleTender = tenders.find(
    tender => !!tender.type && MAIN_API_TENDER_TYPES.includes(tender.type)
  )

  return saleTender || null
}

/**
 * Get the total payment amount from a list of tenders by adding together sale and tip tenders.
 *
 * @param tenders
 *        The list of tenders to get the total payment amount from.
 *
 * @return The total payment amount of the list of tenders.
 */
export const getTotalPaymentAmountFromTenders = (
  tenders: ApiTender[]
): number => {
  const filteredTenders = tenders
    .filter(
      tender =>
        tender.type === 'SALE' ||
        tender.type === 'TIP' ||
        tender.type === 'SURCHARGE'
    )
    .map(tender => vn(tender.amount))

  if (filteredTenders.length > 0) {
    return add(...filteredTenders)
  }
  return 0
}

/**
 * Takes an array of payments and returns an array containing just the ids of those payments.
 *
 * @param payments
 *        The array of payments to get the ids from.
 *
 * @return An array of payment ids
 */
export const getPaymentIds = (payments: Payment[]): string[] => {
  const paymentsIds: string[] = []

  payments.forEach(payment => {
    const { id } = payment

    if (id) {
      paymentsIds.push(id)
    }
  })

  return paymentsIds
}

/**
 * Determines if the current sale has any change to be returned to the customer
 *
 * @return true if this sale has change to be returned, false otherwise
 */
export const hasChange = (change?: Payment | null): boolean => {
  return Boolean(change && Number(change?.amount))
}

/**
 * The flat LSPay cash discount amount for a pre-tax (exclusive) sale total.
 * Rate is a whole-number percentage (e.g. 4 for 4%).
 */
export const getLspCashDiscountAmount = (
  exclusivePrice: number | string,
  rate: number
) => {
  if (rate <= 0 || vn(exclusivePrice).eq(0)) {
    return vn(0)
  }

  return vn(exclusivePrice).times(rate).div(100)
}

type SaleWithLspCashDiscount = {
  totals?: Pick<Totals, 'lspCashDiscount'>
  lspCashDiscountPercentage?: number
}

/**
 * Whether a completed sale had an LSPay cash discount applied to its totals.
 */
export const originalSaleHadLspCashDiscount = (
  sale: SaleWithLspCashDiscount
): boolean => {
  const rate = Number(sale.lspCashDiscountPercentage ?? 0)
  return rate > 0 && !vn(sale.totals?.lspCashDiscount ?? '0').eq(0)
}

/** Whether the sale totals include an applied LSPay cash discount. */
export const hasLspCashDiscountApplied = (
  sale: Pick<ReduxCurrentSale, 'totals'>
): boolean => !vn(sale.totals.lspCashDiscount ?? '0').eq(0)

/** Source id for an LSPay cash discount adjustment (cash payment, else sale). */
export const getLspCashDiscountAdjustmentSourceId = (sale: {
  payments: Payment[]
  localId: string
}): string => {
  const cashPayment = sale.payments.find(payment =>
    isEligibleLspCashPaymentType(payment.paymentType.type_id)
  )

  return cashPayment?.id || cashPayment?.localId || sale.localId
}

/**
 * LSPay cash discount stored on the sale as a negative deduction from totals.
 */
export const calculateLspCashDiscountAmount = (
  exclusivePrice: number | string,
  rate: number
): string => {
  if (rate <= 0 || vn(exclusivePrice).eq(0)) {
    return '0'
  }

  return VendNumber.round(
    getLspCashDiscountAmount(exclusivePrice, rate).neg(),
    2
  ).toString()
}

/** Update outstanding balance with the flat LSPay cash discount and adjusted tax. */
export const applyLspCashDiscountToOutstanding = (
  outstandingBalance: number | string,
  exclusivePrice: number | string,
  rate: number,
  taxAmount?: number | string
) => {
  if (rate <= 0) {
    return vn(outstandingBalance)
  }

  const discountAmount = vn(
    calculateLspCashDiscountAmount(exclusivePrice, rate)
  )

  // Keep legacy behavior unless tax is explicitly provided by the caller.
  if (taxAmount === undefined || taxAmount === null) {
    return vn(outstandingBalance).plus(discountAmount)
  }

  const baseExclusivePrice = vn(exclusivePrice)

  if (baseExclusivePrice.eq(0)) {
    return vn(outstandingBalance).plus(discountAmount)
  }

  const originalTax = vn(taxAmount)
  const discountedExclusivePrice = baseExclusivePrice.plus(discountAmount)
  const discountedTax = originalTax
    .times(discountedExclusivePrice)
    .div(baseExclusivePrice)

  return vn(outstandingBalance)
    .plus(discountAmount)
    .plus(discountedTax.minus(originalTax))
}

/**
 * Apply the LSPay cash discount to a single cash payment amount. The discount
 * is settled in one cash transaction; it is not split across multiple cash
 * payments.
 */
export const applyLspCashDiscount = (
  cashPaymentAmount: number | string,
  exclusivePrice: number | string,
  referenceInclusivePrice: number | string,
  rate: number
) => {
  if (rate <= 0 || vn(referenceInclusivePrice).eq(0)) {
    return vn(cashPaymentAmount)
  }

  const discountAmount = getLspCashDiscountAmount(exclusivePrice, rate)

  return vn(cashPaymentAmount).minus(
    vn(cashPaymentAmount).div(referenceInclusivePrice).times(discountAmount)
  )
}

/**
 * For cash discounting; determines if a cash discounting service fee has been
 * applied to the sale.
 *
 * @returns True if cash discounting is enabled and that a service fee has been
 * applied.
 */
export const hasCashDiscountingServiceFee = (
  registerHasCashDiscounting: boolean,
  sale: Pick<ReduxCurrentSale, 'totals'>
) => {
  return Boolean(
    registerHasCashDiscounting && vn(sale.totals.saleServiceFee).gt(0)
  )
}

const isEligibleLspCashPaymentType = (typeId: number) =>
  typeId === PAYMENT_TYPES.CASH || typeId === PAYMENT_TYPES.CASH_CONCEALED

const isLspCashDiscountRoundingPayment = (typeId: number) =>
  typeId === PAYMENT_TYPES.ROUNDING

/** Cash plus optional register rounding payments that settle a cash sale. */
const getLspCashDiscountSettlementPayments = (payments: Payment[]) =>
  payments.filter(
    payment =>
      isEligibleLspCashPaymentType(payment.paymentType.type_id) ||
      isLspCashDiscountRoundingPayment(payment.paymentType.type_id)
  )

/** Whether an LSPay cash discount rate is configured for the sale. */
export const hasActiveLspCashDiscountPercentage = (
  sale: Pick<ReduxCurrentSale, 'lspCashDiscountPercentage'>
): boolean => Number(sale.lspCashDiscountPercentage ?? 0) > 0

/**
 * Whether register cash discounting should apply to the sale. LSPay cash
 * discounting takes priority when both are configured with a rate > 0.
 */
export const shouldApplyCashDiscountingServiceFee = (
  registerHasCashDiscounting: boolean,
  sale: Pick<ReduxCurrentSale, 'totals' | 'lspCashDiscountPercentage'>
) =>
  hasCashDiscountingServiceFee(registerHasCashDiscounting, sale) &&
  !hasActiveLspCashDiscountPercentage(sale)

/**
 * Whether the recorded payments qualify for applying the LSPay cash discount
 * to the sale total. The discount only applies when the sale is settled in
 * a single cash tender; partial or split payments are excluded.
 */
export const shouldApplyLspCashDiscount = ({
  saleIsReturn,
  lspCashDiscountPercentage,
  payments,
  saleInclusivePrice,
  exclusivePrice,
  tax,
}: {
  saleIsReturn: boolean
  lspCashDiscountPercentage: number
  payments: Payment[]
  saleInclusivePrice: string | number
  exclusivePrice: string | number
  tax: string | number
}) => {
  if (
    saleIsReturn ||
    !lspCashDiscountPercentage ||
    lspCashDiscountPercentage <= 0
  ) {
    return false
  }

  const cashPayments = payments.filter(payment =>
    isEligibleLspCashPaymentType(payment.paymentType.type_id)
  )

  if (cashPayments.length !== 1) {
    return false
  }

  const settlementPayments = getLspCashDiscountSettlementPayments(payments)

  // Only a single cash tender plus optional register rounding is allowed.
  if (settlementPayments.length !== payments.length) {
    return false
  }

  const settlementTotal = settlementPayments.reduce(
    (total, payment) => add(total, payment.amount),
    0
  )

  const discountedSaleTotal = applyLspCashDiscountToOutstanding(
    saleInclusivePrice,
    exclusivePrice,
    lspCashDiscountPercentage,
    tax
  )

  // Compare at 2dp — the precision at which amounts are displayed and paid.
  // The tax adjustment can introduce sub-cent fractions that must not prevent
  // an exact-tender match.
  return vn(settlementTotal).gte(vn(discountedSaleTotal.toFixed(2)))
}

/**
 * Check if the sale has added ABP (Account Balance Payment) but not paid.
 *
 * @return {Boolean} True if ABP has added but not paid, false otherwise
 */
export const isABPAddedButNotPaid = (
  sale: Pick<ReduxCurrentSale, 'payAccountBalance'>
) => {
  if (!sale.payAccountBalance) {
    return false
  }

  return Boolean(
    sale.payAccountBalance.amount && !sale.payAccountBalance.paymentId
  )
}

/**
 * Return true if the sale only have one payment paid for exact ABP(Account Balance Payment) amount
 */
export const hasOnlyOnePaymentForABP = (
  sale: Pick<ReduxCurrentSale, 'payments' | 'payAccountBalance'>
) => {
  return (
    sale.payments.length === 1 &&
    isAccountBalancePaymentPaid(sale.payAccountBalance) &&
    vn(sale.payments[0].amount).eq(sale.payAccountBalance?.amount!)
  )
}

/**
 * Calculate the outstanding balance by subtracting every Klarna payments from
 * Klarna refunds.
 */
export const getKlarnaOutstandingBalance = (
  payments: Payment[],
  originalSalePayments: Payment[]
) => {
  return getCurrentAndOriginalPayments({
    payments,
    originalSalePayments,
  }).reduce((total, payment) => {
    return add(
      total,
      isKlarnaPaymentType(payment.paymentType.type_id) ? payment.amount : 0
    )
  }, 0)
}

/**
 * Return array of current and original payment from sale
 *
 */
export const getCurrentAndOriginalPayments = (
  sale: Pick<ReduxCurrentSale, 'payments' | 'originalSalePayments'>
) => {
  return sale.payments.concat(sale.originalSalePayments)
}

/**
 * Get the sale inclusive price considering ABP (Account Balance Payment).
 */
export const getInclusivePriceConsiderABP = (
  sale: Pick<ReduxCurrentSale, 'payAccountBalance' | 'finals'>
) => {
  // When Account Balance Payment(ABP) is added but not paid, add it to inclusive price
  const inclusivePrice = isABPAddedButNotPaid(sale)
    ? add(sale.finals.inclusivePrice, sale.payAccountBalance?.amount!)
    : sale.finals.inclusivePrice
  return inclusivePrice
}

/**
 * Calculate the outstanding balance by subtracting every non-Klarna payments
 * from non-Klarna refunds.
 */
export const getNonKlarnaOutstandingBalance = (
  sale: Pick<ReduxCurrentSale, 'payments' | 'originalSalePayments'>
) => {
  return getCurrentAndOriginalPayments(sale).reduce((total, payment) => {
    return add(
      total,
      !isKlarnaPaymentType(payment.paymentType.type_id) ? payment.amount : 0
    )
  }, 0)
}

export const roundCash = (
  cashAmount: VendNumber | number | string,
  paymentType: ApiRetailerPaymentType
) => {
  const ROUND_UP_ALWAYS = 'round-up-always'
  const ROUND_MID_UP = 'round-mid-up'
  const ROUND_MID_DOWN = 'round-mid-down'
  const ROUND_DOWN_ALWAYS = 'round-down-always'

  const algorithms = {
    [ROUND_UP_ALWAYS](fractionalPieces: VendNumber, wholePieces: VendNumber) {
      if (!fractionalPieces.isZero()) {
        return wholePieces.plus(1)
      }
      return wholePieces
    },
    [ROUND_MID_UP](fractionalPieces: VendNumber, wholePieces: VendNumber) {
      if (fractionalPieces.greaterThanOrEqualTo(0.5)) {
        return wholePieces.plus(1)
      }
      return wholePieces
    },
    [ROUND_MID_DOWN](fractionalPieces: VendNumber, wholePieces: VendNumber) {
      if (fractionalPieces.greaterThan(0.5)) {
        return wholePieces.plus(1)
      }
      return wholePieces
    },
    [ROUND_DOWN_ALWAYS](fractionalPieces: VendNumber, wholePieces: VendNumber) {
      return wholePieces
    },
  }

  const amount = vn(cashAmount).abs()
  const wasNegative = vn(cashAmount).isNegative()

  // Check if we have cash rounding enabled here
  // If there's no cash rounding, we don't need to do anything
  if (!paymentType.config || !paymentType.config.rounding) {
    return {
      rounded: wasNegative ? amount.mul(-1) : amount,
      remainder: vn(0),
    }
  }

  // To round, we convert the amount remaining into the total number
  // of 'coins' that it represents. Then depending on the remainder round
  // up or down. examples
  // $5.25 => 52.5 10c pieces. $5.20 cash price, 5c rounding payment
  // $7.19 => 71.9 10c pieces. $7.20 cash price, -1c rounding payment
  const roundingUnit = vn(paymentType.config.rounding)

  const roundingMultiple = vn(1).div(roundingUnit)

  // the total number of Xc pieces in the tendered amount
  const scaledTotal = amount.mul(roundingMultiple)
  const wholePieces = scaledTotal.truncated()
  const fractionalPieces = scaledTotal.sub(wholePieces)

  // Now retrieve the algorithm we use
  const algorithmName =
    (paymentType.config.algorithm as keyof typeof algorithms) || ROUND_MID_DOWN
  const roundingAlgorithm = algorithms[algorithmName]
  const roundedWholePieces = roundingAlgorithm(fractionalPieces, wholePieces)

  // Convert the 'pieces math' back into $ by dividing by the rounding multiple
  const roundedTotal = roundedWholePieces.dividedBy(roundingMultiple)

  // The rounding amount is the difference in the 'pieces price' and the amount passed
  const roundingAmount = amount.minus(roundedTotal)
  return {
    rounded: wasNegative ? roundedTotal.mul(-1) : roundedTotal,
    remainder: wasNegative ? roundingAmount.mul(-1) : roundingAmount,
  }
}

/**
 * Remove the active lspay transaction details from local storage
 */
export const removeActiveTransactionDetails = () => {
  // We don't need to keep the active transaction details in storage anymore
  // already recorded the transaction to the sale
  removeFromStorage(ACTIVE_LSPAY_TRANSACTION)
}

export const convertTransactionToApiTransaction = (
  transaction: Transaction,
  {
    registerId,
    retailerPaymentType,
    currency,
  }: {
    registerId: string
    retailerPaymentType: ApiRetailerPaymentType
    currency: string
  }
): ApiTransaction => {
  return {
    id: transaction.id,
    retailer_payment_type_id: retailerPaymentType.id,
    register_id: registerId,
    register_sale_payment_id: transaction.registerSalePaymentID,
    payment_type_id: retailerPaymentType.type_id,
    status: transaction.status,
    provider_fields: null, // only provider_fields of tenders will be used for Swiper payment, so omitting transaction provider_fields
    tenders: transaction.tenders.map(
      (tender): ApiTender => ({
        id: tender.id,
        amount: tender.amount,
        status: tender.status,
        currency,
        emv: {
          tags: tender.emv.tags,
          aid: tender.emv.aid,
          application_label: tender.emv.applicationLabel,
          application_preferred_name: tender.emv.applicationPreferredName,
          cryptogram_type: tender.emv.cryptogramType,
          cryptogram: tender.emv.cryptogram,
          pin_statement: tender.emv.pinStatement,
        },
        type: tender.type,
        external_transaction_id: tender.externalTransactionID,
        card_details: {
          auth_code: tender.cardDetails.authCode,
          auth_result: tender.cardDetails.authResult,
          card: {
            brand: tender.cardDetails.card.brand,
            type: tender.cardDetails.card.type,
            last_four: tender.cardDetails.card.lastFour,
            fingerprint: tender.cardDetails.card.fingerPrint,
          },
          entry_method: tender.cardDetails.entryMethod,
        },
        provider_fields: tender.providerFields,
        created_at: tender.createdAt,
      })
    ),
  }
}

/**
 * Check if a payment was processed via Processing Service.
 * Processing Service payments have a 'lspay_payment_id' in their providerFields.
 *
 * @param payment The payment to check
 * @returns True if the payment was a Processing Service payment, false otherwise
 */
export const isPaymentProcessingService = (payment: Payment): boolean => {
  if (!payment.providerFields) {
    return false
  }

  return 'lspay_payment_id' in payment.providerFields
}

/**
 * Check if any of the given payments were processed via Processing Service.
 *
 * @param payments Array of payments to check
 * @returns True if any payment was a Processing Service payment, false otherwise
 */
export const hasAnyProcessingServicePayment = (
  payments: Payment[]
): boolean => {
  return payments.some(isPaymentProcessingService)
}
